<template>
    <LoadingView v-if="loading" />
    <figure class="w-full h-screen relative overflow-hidden" :class="$attrs.class">
        <div class="absolute top-0 left-0 bottom-0 h-full transition-all flex items-start z-10" :class="openDrawer ? 'ml-0' : '-ml-96'">
            <div class="bg-white rounded-md shadow-lg m-4 order-1 aspect-square p-1 text-center justify-center cursor-pointer" @click="openDrawer = !openDrawer"><span class="far" :class="openDrawer ? 'fa-close' : 'fa-ellipsis-vertical'"  title="Toggle Filters"></span></div>
            <aside class="flex flex-col gap-4 h-full bg-white p-4 w-96 overflow-y-auto" >
                <section class="flex flex-col gap-4" v-if="$slots.filters">
                    <h3 class="text-bhav-blue-600">Map Filters</h3>
                    <slot name="filters"></slot>
                </section>
                <section class="flex flex-col gap-4" v-if="inspector">
                    <h3 class="text-bhav-blue-600">Map Inspector</h3>
                    <dl>
                        <dt class="text-oakwood-gray-500 text-sm font-bold">Coordinates</dt>
                        <dd class="text-oakwood-gray-900 text-sm">Lat: {{ cursor?.lat }}</dd>
                        <dd class="text-oakwood-gray-900 text-sm">Lng: {{ cursor?.lng }}</dd>
                        <dt class="text-oakwood-gray-500 text-sm font-bold mt-4">Zoom Level</dt>
                        <dd class="text-oakwood-gray-900 text-sm">{{ Math.round(zoomLevel * 100)/100 }}</dd>
                    </dl>
                </section>
                <section class="flex flex-col gap-4">
                    <h3 class="text-bhav-blue-600">Legend</h3>
                    <slot name="legend"></slot>
                </section>
            </aside>
        </div>
        <div ref="mapContainer" class="w-full h-full sm:order-2 flex flex-col text-xs text-oakwood-gray-500"></div>
        <slot v-if="!hasFeatures && !loading" name="none">
            <div class="absolute z-20 inset-0 bg-oakwood-gray-100 opacity-80 flex items-center justify-center">
                There was no data to display.
            </div>
        </slot>
        <figcaption class="sr-only">This is the map of the parcels for this community.</figcaption>
    </figure>
</template>

<script>
import mapboxgl from "mapbox-gl";
import 'mapbox-gl/dist/mapbox-gl.css';
import LoadingView from './LoadingView.vue';
mapboxgl.accessToken = import.meta.env.VITE_APP_MAPBOX_ACCESS_TOKEN;
let map;

export default {
  components: { LoadingView },

    emits: [
        'selected'
    ],
    data: () => ({
        initialLocation: null,
        hover: null,
        cursor: {
            lat: null,
            lng: null
        },
        zoomLevel: 0,
        openDrawer: false
    }),
    methods: {
        initialize() {
            map = new mapboxgl.Map({
                container: this.$refs.mapContainer,
                style: import.meta.env.VITE_APP_MAPBOX_STYLE,
                center: this.centroid,
                bearing: this.bearing,
                pitch: this.pitch,
                zoom: this.zoom,
            })
            .on('mousemove', (e) => {
                this.setCursor(e);
            })
            .on('zoomend', (e) => {
                this.zoomLevel = map.getZoom()
            })
            .on('style.load', () => {
                this.addSources();
                this.addLayers();
            })
            .on('click', (e) => {
                const bbox = [
                    [e.point.x, e.point.y],
                    [e.point.x, e.point.y]
                ];

                const selectedFeatures = map.queryRenderedFeatures(bbox);
                // console.log(selectedFeatures);
            });
        },
        addSources() {
            // if(!this.hasFeatures) return;
            this.sources.forEach(source => {
                map.addSource(
                    source.id, 
                    {
                        type: 'geojson',
                        data: {
                            type: 'FeatureCollection',
                            name: source.name,
                            crs: {
                                type: 'name',
                                properties: {
                                    name: source.name
                                } 
                            },
                            features: source.features || []
                        },
                        cluster: source?.cluster || false,
                        clusterMaxZoom: source?.clusterMaxZoom || 0,
                        clusterRadius: source?.clusterRadius || 50
                    }
                );
            });
        },
        updateSources() {
            // if(!this.hasFeatures) return;
            this.sources.forEach(source => {
                map.getSource( source.id ).setData( 
                    {
                        type: 'FeatureCollection',
                        name: source.name,
                        crs: {
                            type: 'name',
                            properties: {
                                name: source.name
                            } 
                        },
                        features: source.features || [] 
                    }
                );
            });
        },
        addLayers() {
            // if(!this.hasFeatures) return;
            this.layers.forEach(layer => {
                const settings = {
                    id: layer.id,
                    type: layer.type,
                    source: layer.source,
                    paint: layer.paint || {},
                    minzoom: layer.minzoom || 0,
                }

                if(layer?.layout) settings.layout = layer.layout;
                if(layer?.filter) settings.filter = layer.filter;

                map.addLayer(
                    settings,
                    // layer.before || "settlement-major-label"
                );

                map.on('click', layer.id, (e) => {
                    if(this.clickable || e.features.flatMap(feature => feature.properties?.clickable ?? false).includes(true)) {
                        this.$emit('selected', e.features[0]);
                    }
                });

                map.on('mousemove', layer.id, (e) => {
                    if(this.hover !== e.features[0].properties.id && (this.clickable || e.features.flatMap(feature => feature.properties?.clickable ?? false).includes(true))) {
                        this.hover = e.features[0].properties.id;
                        map.getCanvas().style.cursor = 'pointer';
                    }
                })
                map.on('mouseleave', layer.id, () => {
                    map.getCanvas().style.cursor = '';
                    this.hover = null;
                })
                map.on('load', () => {
                    this.fitBounds();
                })
            });
        },
        togglePOIs() {
            const layers = map.getStyle().layers.filter(layer => {
                return [
                    'poi-airport', 
                    'poi-auto', 
                    'poi-bank', 
                    'poi-bar', 
                    'poi-coffee', 
                    'poi-entertainment', 
                    'poi-food', 
                    'poi-fuel', 
                    'poi-generic', 
                    'poi-grocery', 
                    'poi-library', 
                    'poi-lodging', 
                    'poi-medical', 
                    'poi-parks-scale-1', 
                    'poi-parks-scale-2', 
                    'poi-pharmacy', 
                    'poi-public-services', 
                    'poi-rail', 
                    'poi-retail', 
                    'poi-schools', 
                    'water-label', 
                    'waterway-label'
                ].includes(layer.id);
            });
            layers.forEach(layer => {
                map.setLayoutProperty(layer.id, 'visibility', (this.pois ? 'visible' : 'none'));
            });
        },
        fitBounds() {
            if(!this.coordinates.length) return;
            let bounds = new mapboxgl.LngLatBounds();

            this.coordinates
                .forEach(feature => {
                    if((90 < feature.lat || -90 > feature.lat) || (180 < feature.lng || -180 > feature.lng)) {
                        console.error('Invalid LngLat', feature);
                        return;
                    }
                    bounds.extend(feature);
                });

            map.fitBounds(bounds, {
                pitch: this.pitch,
                bearing: this.bearing,
                padding: this.padding,
                linear: false
            });
        },
        setCursor(data) {
            this.cursor = {
                lat: data.lngLat.lat,
                lng: data.lngLat.lng
            }
        }
    },
    computed: {
        coordinates() {
            let i = 0;
            let flatten = (arr, catcher) => {
                return arr.reduce((acc, cur, idx) => 
                    {
                        if(Array.isArray(cur)) {
                            flatten(cur, acc);
                        } else {
                            if(idx > 1) return; // no z-indices
                            if(acc[i] === undefined) {
                                acc[i] = {
                                    lng: null,
                                    lat: null,
                                    alt: null
                                }
                            }
                            switch(idx) {
                                case 0 :
                                    acc[i].lng = cur;
                                    break;
                                case 1 :
                                    acc[i].lat = cur;
                                    break;
                                case 2 :
                                    acc[i].alt = cur;
                                    break;
                                default :
                                    return;
                            }
                            i = arr.length === idx + 1 ? i+1 : i;
                        }
                        return acc;
                    },
                    catcher
                );
            }

            return flatten(this.sources.flatMap(source => source.features.map(feature => feature.geometry.coordinates)), []);
        },
        centroid() {
            if(!this.coordinates.length) return this.center;
            let lng = this.coordinates.map(coordinate => coordinate?.lng),
            lat = this.coordinates.map(coordinate => coordinate?.lat);

            return {
                lng: ( Math.max(...lng) + Math.min(...lng) ) / 2,
                lat: ( Math.max(...lat) + Math.min(...lat) ) / 2
            };
        },
        hasFeatures() {
            return this.sources.filter(source => (source?.features ?? []).length > 0).length > 0;
        }
    },
    mounted() {
        this.initialize();

    },
    created(){
        this.initialLocation = {
            ...this.center,
            bearing: this.bearing,
            pitch: this.pitch,
            zoom: this.zoom
        };
    },
    watch: {
        hover: {
            handler(newval, oldval) {
                if(oldval !== null) {
                    map.setFeatureState(
                        {source: 'lots', id: oldval ?? null},
                        {hover: false}
                    );
                }
                map.setFeatureState(
                    {source: 'lots', id: newval ?? null},
                    {hover: true}
                );
            }
        },
        sources: {
            handler() {
                this.updateSources();
                this.fitBounds();
            }
        },
        pois: {
            handler() {
                this.togglePOIs();
            }
        },
    },  

    unmounted() {
        map.remove();
        map = null;
    },
    props: {
        center: {
            type: Object,
            default: () => ({lng: -98.72314453125, lat: 38.53097889440025})
        },
        bearing: {
            type: Number,
            default: 0
        },
        pitch: {
            type: Number,
            default: 10
        },
        zoom: {
            type: Number,
            default: 14
        },
        show: {
            type: Object,
            default: () => ({
                roads: false,
                common: false,
                new: true,
                released: true,
                sold: true,
            })
        },
        padding: {
            type: Number,
            default: 50
        },
        sources: {
            type: Array,
            default: () => []
        },
        layers: {
            type: Array,
            default: () => []
        },
        drawer: {
            type: Boolean,
            default: true
        },
        inspector: {
            type: Boolean,
            default: true
        },
        pois: {
            type: Boolean,
            default: true
        },
        loading: {
            type: Boolean,
            default: false
        },
        clickable: {
            type: Boolean,
            default: false
        }
    },
}
</script>

<style>
:root .mapboxgl-ctrl-logo {
    @apply hidden;
}
:root .mapboxgl-ctrl-attrib-inner *  {
    @apply text-oakwood-gray-300;
}
</style>